// #334 — pure view helpers for the operator dashboard's telemetry block
// (`summary.telemetry` + `summary.energy` from GET /api/usage/summary).
//
// Since round 3 (2026-10-30) the backend serves REAL figures (no small-group
// suppression); they carry no identity or content. A null still means "no
// figure" (e.g. an unreadable table) and renders as WITHHELD ("—"), never 0.
import { PILLAR_ORDER } from './energyPalette';

export const WITHHELD = '—';

export const compactNumber = (n) => {
    if (n == null) return WITHHELD;
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return Number(n).toLocaleString();
};

export const formatBytes = (n) => {
    if (n == null) return WITHHELD;
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = Number(n);
    let i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    return `${i === 0 ? v : v.toFixed(1)} ${units[i]}`;
};

export const formatAverage = (n) => (n == null ? WITHHELD : (Math.round(n * 10) / 10).toLocaleString());

const metric = (telemetry, key) => telemetry?.metrics?.[key] || null;

export const metricValue = (telemetry, key) => metric(telemetry, key)?.value ?? null;

export const metricAverage = (telemetry, key) => metric(telemetry, key)?.avg_per_contributor ?? null;

// Per-module rows for the "Modules" table: actions in the period (flow) and the
// average held per person (stock). `extra` = a module-specific breakdown.
export const MODULE_ROWS = [
    { module: 'finance', stock: 'finance.entries_total', extra: [
        { key: 'finance.expenses_created', labelKey: 'auxTelemetryExpenses', label: 'Expenses' },
        { key: 'finance.income_created', labelKey: 'auxTelemetryIncome', label: 'Income' },
    ] },
    { module: 'reminders', stock: 'reminders.active_total', extra: [] },
    { module: 'notes', stock: 'notes.notes_total', extra: [] },
    { module: 'boards', stock: 'boards.boards_total', extra: [
        { key: 'boards.tickets_created', labelKey: 'auxTelemetryTickets', label: 'Tickets' },
    ] },
    { module: 'calendar', stock: 'calendar.events_total', extra: [] },
    { module: 'files', stock: 'files.files_total', extra: [] },
];

export const moduleRows = (telemetry) => MODULE_ROWS.map((row) => ({
    module: row.module,
    actions: metricValue(telemetry, `${row.module}.activity`),
    avgPerUser: metricAverage(telemetry, row.stock),
    extra: row.extra.map((e) => ({ ...e, value: metricValue(telemetry, e.key) })),
}));

// Leaderboard bars: width relative to the top module; a withheld module gets
// no bar (0) rather than a guessed one.
export const leaderboardBars = (telemetry) => {
    const rows = telemetry?.leaderboard || [];
    const max = Math.max(0, ...rows.map((r) => r.actions || 0));
    return rows.map((r, i) => ({
        ...r,
        rank: i + 1,
        pct: r.actions != null && max > 0 ? (100 * r.actions) / max : 0,
    }));
};

export const CHANNELS = [
    { id: 'main_chat', labelKey: 'auxTelemetryMainChat', label: 'Main Chat', messages: 'chat.messages_main_chat', linked: null },
    { id: 'telegram', labelKey: 'auxTelemetryTelegram', label: 'Telegram', messages: 'chat.messages_telegram', linked: 'chat.linked_telegram' },
];

export const channelRows = (telemetry) => CHANNELS.map((c) => ({
    ...c,
    messagesValue: metricValue(telemetry, c.messages),
    linkedValue: c.linked ? metricValue(telemetry, c.linked) : null,
}));

// Daily input/output token bars from `summary.daily` (already account-wide):
// oldest first, heights relative to the busiest day. `cached` is carried for
// the tooltip only — and kept null when the API did not send it, so a missing
// figure reads "—", never a made-up 0.
export const dailyTokenBars = (daily) => {
    const days = Object.entries(daily || {})
        .filter(([day]) => day !== 'unknown')
        .sort((a, b) => a[0].localeCompare(b[0]));
    const max = Math.max(0, ...days.map(([, d]) => Math.max(d.prompt_tokens || 0, d.completion_tokens || 0)));
    return days.map(([day, d]) => ({
        day,
        input: d.prompt_tokens || 0,
        output: d.completion_tokens || 0,
        cached: d.cached_tokens ?? null,
        inputPct: max > 0 ? (100 * (d.prompt_tokens || 0)) / max : 0,
        outputPct: max > 0 ? (100 * (d.completion_tokens || 0)) / max : 0,
    }));
};

// Period totals shown above the chart. Same day filter as the bars, so the
// headline always equals the sum of what is drawn. `cached` stays null when no
// day carried a cached figure at all.
export const tokenTotals = (daily) => {
    const days = Object.entries(daily || {}).filter(([day]) => day !== 'unknown').map(([, d]) => d || {});
    const anyCached = days.some((d) => d.cached_tokens != null);
    return {
        input: days.reduce((s, d) => s + (d.prompt_tokens || 0), 0),
        output: days.reduce((s, d) => s + (d.completion_tokens || 0), 0),
        cached: anyCached ? days.reduce((s, d) => s + (d.cached_tokens || 0), 0) : null,
    };
};

// Axis label: compactNumber without a trailing ".0" ("1M", not "1.0M").
export const axisLabel = (n) => compactNumber(n).replace(/\.0(?=[KM]$)/, '');

const NICE_STEPS = [1, 2, 2.5, 5, 10];

// Y-axis ticks for the token chart: 0 to a round ceiling at or above `max`,
// about `target` intervals. `max` is the scale the bars are drawn against.
// An all-zero period gets a single 0 tick and a scale of 0 (bars stay flat).
export const tokenAxisTicks = (max, target = 4) => {
    const m = Number(max);
    if (!(m > 0) || !Number.isFinite(m)) return { max: 0, ticks: [{ value: 0, label: '0', pct: 0 }] };
    const raw = m / Math.max(1, target);
    const magnitude = 10 ** Math.floor(Math.log10(raw));
    const fraction = raw / magnitude;
    const step = (NICE_STEPS.find((s) => fraction <= s) ?? 10) * magnitude;
    const top = Math.ceil(m / step - 1e-9) * step;
    const values = [];
    for (let v = 0; v <= top + step / 2; v += step) values.push(Number(v.toPrecision(12)));
    const scale = values[values.length - 1];
    return { max: scale, ticks: values.map((v) => ({ value: v, label: axisLabel(v), pct: (100 * v) / scale })) };
};

// A bar's height (0–100) against the axis scale.
export const tokenBarPct = (value, scaleMax) => {
    const v = Number(value);
    const s = Number(scaleMax);
    if (!(v > 0) || !(s > 0)) return 0;
    return Math.min(100, (100 * v) / s);
};

// Which day columns carry an x-axis date label: at most `maxLabels`, evenly
// spaced and anchored on the NEWEST day (always labelled), so labels never
// pile on top of each other however long the range is.
export const tokenLabelIndices = (n, maxLabels = 7) => {
    const count = Math.max(0, Math.floor(Number(n) || 0));
    if (count === 0) return [];
    const stride = Math.max(1, Math.ceil(count / Math.max(1, maxLabels)));
    const out = [];
    for (let i = count - 1; i >= 0; i -= stride) out.unshift(i);
    return out;
};

const MONTHS = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'],
};

// "2026-10-27" → "27 Oct" (en) / "27 oct" (es). Calendar day as sent — no
// timezone arithmetic, so a viewer's offset can never shift the label.
export const shortDayLabel = (iso, language = 'en') => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    if (!m) return String(iso || '');
    const months = MONTHS[language] || MONTHS.en;
    return `${Number(m[3])} ${months[Number(m[2]) - 1] || m[2]}`;
};

// "2026-10-27" → "27 Oct 2026" for the tooltip heading.
export const longDayLabel = (iso, language = 'en') => {
    const m = /^(\d{4})-/.exec(String(iso || ''));
    return m ? `${shortDayLabel(iso, language)} ${m[1]}` : String(iso || '');
};

// Exact figure with thousands separators (tooltip / totals). Null stays "—".
export const exactNumber = (n, language = 'en') => {
    if (n == null || !Number.isFinite(Number(n))) return WITHHELD;
    return Number(n).toLocaleString(language === 'es' ? 'es-ES' : 'en-US', { useGrouping: true });
};

// ── Energy Points consumed (round 3) ─────────────────────────────────────────
// `summary.energy` = { total_pts, pillars: [{pillar, pts}], daily: [{date,
// total_pts, pillars}] }, platform-wide, same ledger rows as the Energy page.
// Pillars are always rendered in PILLAR_ORDER whatever the response order.

const pillarPts = (list, pillar) => Number((list || []).find((x) => x.pillar === pillar)?.pts) || 0;

export const energyTotals = (energy) => ({
    total: Number(energy?.total_pts) || 0,
    pillars: PILLAR_ORDER.map((pillar) => ({ pillar, pts: pillarPts(energy?.pillars, pillar) })),
});

export const energyDailyBars = (energy) => (energy?.daily || []).map((d) => ({
    day: d.date,
    total: Number(d.total_pts) || 0,
    pillars: PILLAR_ORDER.map((pillar) => ({ pillar, pts: pillarPts(d.pillars, pillar) })),
}));

// Points with two decimals ("1,234.50"), like the Energy page. Null → "—".
export const formatPoints = (n, language = 'en') => {
    if (n == null || !Number.isFinite(Number(n))) return WITHHELD;
    return Number(n).toLocaleString(language === 'es' ? 'es-ES' : 'en-US',
        { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true });
};
